// Turn a supabase.functions.invoke() failure into something a customer can act on.
//
// On any non-2xx, supabase-js throws away the response body and hands back a
// FunctionsHttpError whose `.message` is the fixed string "Edge Function returned a
// non-2xx status code". Callers that surface `e.message` therefore show that sentence
// no matter what actually went wrong.
//
// It cost a real customer. Michael at Legacies Acquisitions, 2026-08-26: five 403s on
// verify-sms-consent at the last step of A2P registration. The function had answered with
// a perfectly clear body — "Terms of Service acceptance required." from authUtils.ts:76,
// alongside code TERMS_NOT_ACCEPTED — and none of it reached the screen. He saw the
// generic string, could not act on it, and emailed to cancel.
//
// The body IS still available: supabase-js attaches the raw Response as `err.context`.
// This reads it, preferring a JSON `error`/`message` field and falling back to plain text
// (several edge functions answer auth failures with a bare string body).
//
// Always resolves — never throws — because it runs inside a catch block that is already
// handling a failure. If nothing readable comes back, the caller's own fallback is used.
export async function readInvokeError(err, fallback = 'Something went wrong.') {
  const generic = /non-2xx status code/i;
  const direct = typeof err?.message === 'string' && !generic.test(err.message) ? err.message : null;

  const res = err?.context;
  if (res && typeof res.text === 'function') {
    try {
      // .clone() so a caller that also wants to read the body still can.
      const raw = await (typeof res.clone === 'function' ? res.clone() : res).text();
      const trimmed = (raw || '').trim();
      if (trimmed) {
        try {
          const parsed = JSON.parse(trimmed);
          const msg = parsed?.error || parsed?.message;
          if (msg) return String(msg);
        } catch {
          // Not JSON. A bare-string body is the normal shape for auth refusals.
          if (trimmed.length <= 300 && !trimmed.startsWith('<')) return trimmed;
        }
      }
    } catch {
      // Body already consumed or unreadable — fall through.
    }
  }
  return direct || fallback;
}

export default readInvokeError;
